import {Component, computed, inject, model, output, signal} from '@angular/core';
import {ButtonComponent} from '../../../../../shared/components/button/button.component';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {Modal} from '../../../../../shared/components/modal/modal';
import {LookupStore} from '../../../../../shared/store/lookup.store';
import {BadgeComponent} from '../../../../../shared/components/badge/badge.component';
import {IconsComponent} from '../../../../../shared/components/icons/icons.component';
import {IAlternateVehicleFitment} from '../../../../../types/IAlternateVehicleFitment';
import {TitleCasePipe} from '@angular/common';
import {Hr} from '../../../../../shared/components/hr/hr';

@Component({
  selector: 'app-alternate-fitments-modal',
  imports: [
    ButtonComponent,
    FormsModule,
    Modal,
    ReactiveFormsModule,
    BadgeComponent,
    IconsComponent,
    TitleCasePipe,
    TitleCasePipe,
    Hr,
  ],
  templateUrl: './alternate-fitments-modal.html',
  styleUrl: './alternate-fitments-modal.css',
  standalone: true
})
export class AlternateFitmentsModal {
  isOpen = model<boolean>(false);

  onSubmitSearch = output<IAlternateVehicleFitment>();

  public loading = signal<boolean>(false);
  public selectedAlternateFitment = signal<IAlternateVehicleFitment | null>(null);

  private lookupStore = inject(LookupStore);

  groupedAlternates = computed(() => {
    const fitment = this.lookupStore.vehicle.selected_fitment();
    const stockItems: IAlternateVehicleFitment[] = [];

    if (fitment) {
      const isStaggered = fitment.rear_tire_size && fitment.front_tire_size !== fitment.rear_tire_size;

      stockItems.push({fg_chassis_id: fitment.fg_chassis_id ?? 0, type: 'stock', position: isStaggered ? 'front' : 'both', wheel_size: `${fitment.rim_width}x${parseFloat(String(fitment.rim_diameter))}`, tire_size: fitment.front_tire_size, min_offset: 0, max_offset: 0});


      if (isStaggered) {
        stockItems.push({fg_chassis_id: fitment.fg_chassis_id ?? 0, type: 'stock', position: 'rear', wheel_size: `${fitment.rim_width}x${fitment.rim_diameter}`, tire_size: fitment.rear_tire_size, min_offset: 0, max_offset: 0});
      }
    }

    return [
      {
        type: 'minus',
        label: 'Minus Sizing',
        description: 'Smaller wheel diameter — better ride, lower cost',
        items: this.lookupStore.vehicle.alternates()?.filter(a => a.type === 'minus') || [],
      },
      {
        type: 'stock',
        label: 'OEM Fitment',
        description: 'Factory spec',
        items: stockItems,
      },
      {
        type: 'plus',
        label: 'Plus Sizing',
        description: 'Larger wheel diameter — sharper handling, stance',
        items: this.lookupStore.vehicle.alternates()?.filter(a => a.type === 'plus') || [],
      },
    ];
  });

  private parseRollingDiameter(size: string): number | null {
    const metric = size.match(/^(\d+)\/(\d+)R(\d+)$/);
    if (metric) {
      const width = parseFloat(metric[1]);
      const ratio = parseFloat(metric[2]) / 100;
      const rim = parseFloat(metric[3]);
      return (width * ratio * 2) + (rim * 25.4);
    }

    const flotation = size.match(/^(\d+)x[\d.]+R(\d+)$/);
    if (flotation) {
      return parseFloat(flotation[1]) * 25.4;
    }

    return null;
  }

  private rollingDiameterDiff(tireSize: string, position: string = 'both'): number | null {
    const fitment = this.lookupStore.vehicle.selected_fitment();
    const isStaggered = fitment?.rear_tire_size && fitment.rear_tire_size !== fitment.front_tire_size;
    const stockSize = (isStaggered && position === 'rear') ? fitment!.rear_tire_size : fitment?.front_tire_size || '';
    const stock = this.parseRollingDiameter(stockSize);
    const alt = this.parseRollingDiameter(tireSize);
    if (!stock || !alt) return null;
    return ((alt - stock) / stock) * 100;
  }

  calculateRollingDiameterDiff(tireSize: string, position: string = 'both'): string {
    const diff = this.rollingDiameterDiff(tireSize, position);
    if (diff === null) return '';
    const rounded = Math.round(diff * 10) / 10;
    return diff > 0 ? `+${rounded}%` : `${rounded}%`;
  }

  getDiameterDifferenceBadgeVariant(tireSize: string, position: string = 'both'): 'red' | 'blue' | 'grey' {
    const diff = this.rollingDiameterDiff(tireSize, position);
    if (diff === null) return 'grey';
    if (diff > 0) return 'red';
    if (diff < 0) return 'blue';
    return 'grey';
  }

  getPositionBadgeVariant(position: string): 'blue' | 'green' | 'grey' {
    switch (position) {
      case 'front': return 'blue';
      case 'rear': return 'green';
      default: return 'grey';
    }
  }

  onSubmit() {
    const alternate = this.selectedAlternateFitment();

    if (alternate) {
      this.onSubmitSearch.emit(alternate);
    }
  }
}
